import { useEffect, useRef } from 'react';
import { View, Text, TextInput, Pressable, ScrollView, Animated } from 'react-native';
import { ArrowRight } from 'lucide-react-native';
import { footballerQuizBoardStyles as styles } from './FootballerQuizBoard.styles';
import { FootballerFlag } from './FootballerFlag';
import {
  FOOTBALLER_COLORS as C,
  FOOTBALLER_COLUMNS,
  FOOTBALLER_TEST_IDS as IDS,
  type FootballerMark,
} from '../../../src/quiz/constants/footballerQuiz.constants';
import type { FootballerGuessRow, FootballerSuggestion } from '../../../src/quiz/helpers/footballerQuizApi';

interface FootballerQuizBoardProps {
  streak: number;
  maxGuesses: number;
  guessesUsed: number;
  guessesLeft: number;
  guesses: FootballerGuessRow[];
  input: string;
  suggestions: FootballerSuggestion[];
  showDropdown: boolean;
  busy: boolean;
  onInputChange: (text: string) => void;
  onSelectSuggestion: (s: FootballerSuggestion) => void;
  onSubmit: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

const AMBER_TEXT = '#3A2C05';

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

/** Tile text color by mark + content kind. */
function textColor(mark: FootballerMark, kind: 'num' | 'text'): string {
  if (mark === 'green') return C.tileGreenText;
  if (mark === 'amber') return AMBER_TEXT;
  return kind === 'num' ? C.tileNumText : C.tileTextDim;
}

function tileStyle(mark: FootballerMark) {
  return [styles.tile, mark === 'green' && styles.tileGreen, mark === 'amber' && styles.tileAmber];
}

function GuessRow({ row, isNew }: { row: FootballerGuessRow; isNew: boolean }) {
  const anim = useRef(new Animated.Value(isNew ? 0 : 1)).current;
  useEffect(() => {
    if (isNew) {
      Animated.timing(anim, { toValue: 1, duration: 380, useNativeDriver: true }).start();
    }
  }, [isNew, anim]);
  const animatedStyle = {
    opacity: anim,
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [12, 0] }) }],
  };

  return (
    <Animated.View style={[styles.rowWrap, animatedStyle]} testID={IDS.row}>
      <Text style={styles.rowName}>{row.name.toUpperCase()}</Text>
      <View style={styles.tilesRow}>
        <View style={tileStyle(row.nat.mark)}>
          <FootballerFlag country={row.nat.country} width={30} height={22} />
        </View>
        <View style={tileStyle(row.league.mark)}>
          <FootballerFlag country={row.league.country} width={26} height={19} />
          <Text style={[styles.tileDiv, { color: textColor(row.league.mark, 'text') }]}>#{row.league.division}</Text>
        </View>
        <View style={tileStyle(row.team.mark)}>
          <Text style={[styles.tileClub, { color: textColor(row.team.mark, 'text') }]} numberOfLines={3}>
            {row.team.club ?? '—'}
          </Text>
        </View>
        <View style={tileStyle(row.pos.mark)}>
          <Text style={[styles.tilePos, { color: textColor(row.pos.mark, 'num') }]}>{row.pos.bucket ?? '—'}</Text>
        </View>
        <View style={tileStyle(row.age.mark)}>
          <Text style={[styles.tileAge, { color: textColor(row.age.mark, 'num') }]}>{row.age.value ?? '—'}</Text>
          {row.age.arrow ? (
            <Text style={[styles.tileArrow, { color: row.age.arrow === '▲' ? C.arrowUp : C.arrowDown }]}>{row.age.arrow}</Text>
          ) : null}
        </View>
      </View>
    </Animated.View>
  );
}

export function FootballerQuizBoard({
  streak,
  maxGuesses,
  guessesUsed,
  guessesLeft,
  guesses,
  input,
  suggestions,
  showDropdown,
  busy,
  onInputChange,
  onSelectSuggestion,
  onSubmit,
  onFocus,
  onBlur,
}: FootballerQuizBoardProps) {
  const scrollRef = useRef<ScrollView>(null);
  const prevCount = useRef(guesses.length);
  useEffect(() => {
    if (guesses.length > prevCount.current) {
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 60);
    }
    prevCount.current = guesses.length;
  }, [guesses.length]);

  const hasInput = input.trim().length > 0;
  const dots = Array.from({ length: maxGuesses }, (_, i) => i);

  return (
    <View style={styles.root} testID={IDS.board}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.pill}>
          <Text style={styles.pillFlame}>🔥</Text>
          <Text style={styles.pillStreak}>{streak}</Text>
        </View>
        <View style={styles.titleWrap}>
          <Text style={styles.title}>FOOTBALLER</Text>
          <Text style={styles.subtitle}>GUESS THE PLAYER</Text>
        </View>
        <View style={styles.pill}>
          <View style={styles.pillDot} />
          <Text style={styles.pillGuesses}>{guessesLeft}/{maxGuesses}</Text>
        </View>
      </View>

      {/* Mystery card */}
      <View style={styles.mysteryCard} testID={IDS.mysteryCard}>
        <View style={styles.mysteryGlow}>
          <Text style={styles.mysteryQ}>?</Text>
        </View>
        <View style={styles.mysteryBody}>
          <Text style={styles.mysteryTitle}>Today&apos;s Footballer</Text>
          <Text style={styles.mysterySub}>Guess {guessesUsed} of {maxGuesses}</Text>
          <View style={styles.dotsRow}>
            {dots.map((i) => (
              <View key={i} style={[styles.dot, i < guessesUsed && styles.dotFilled]} />
            ))}
          </View>
        </View>
      </View>

      {/* Sticky column labels */}
      <View style={styles.labelsRow}>
        {FOOTBALLER_COLUMNS.map((col) => (
          <View key={col.key} style={styles.labelCell}>
            <Text style={styles.labelText}>{col.label}</Text>
          </View>
        ))}
      </View>

      {/* Guessed rows */}
      <ScrollView ref={scrollRef} style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        {guesses.map((row, i) => (
          <GuessRow key={`${row.playerId}-${i}`} row={row} isNew={i === guesses.length - 1} />
        ))}
      </ScrollView>

      {/* Search bar */}
      <View style={styles.searchBar}>
        {showDropdown && suggestions.length > 0 ? (
          <View style={styles.dropdown} testID={IDS.dropdown}>
            {suggestions.map((s) => (
              <Pressable
                key={s.id}
                testID={IDS.suggestion}
                onPress={() => onSelectSuggestion(s)}
                style={({ pressed }) => [styles.suggestion, pressed && styles.pressed]}
              >
                <View style={styles.suggestionAvatar}>
                  <Text style={styles.suggestionInitials}>{initials(s.name)}</Text>
                </View>
                <View style={styles.suggestionBody}>
                  <Text style={styles.suggestionName} numberOfLines={1}>{s.name}</Text>
                  <View style={styles.suggestionMetaRow}>
                    <FootballerFlag country={s.nationality} width={16} height={12} />
                    <Text style={styles.suggestionMeta} numberOfLines={1}>
                      {s.nationality}{s.club ? ` · ${s.club}` : ''}
                    </Text>
                  </View>
                </View>
                {s.position ? <Text style={styles.suggestionPos}>{s.position}</Text> : null}
              </Pressable>
            ))}
          </View>
        ) : null}

        <View style={styles.searchRow}>
          <TextInput
            testID={IDS.searchInput}
            style={styles.input}
            value={input}
            onChangeText={onInputChange}
            onSubmitEditing={onSubmit}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder="Search for a footballer..."
            placeholderTextColor={C.labelDim}
            returnKeyType="search"
            autoCorrect={false}
            autoCapitalize="words"
            editable={!busy}
          />
          <Pressable
            testID={IDS.submitButton}
            onPress={onSubmit}
            disabled={!hasInput || busy}
            style={({ pressed }) => [styles.submitBtn, hasInput && styles.submitBtnActive, pressed && styles.pressed]}
          >
            <ArrowRight size={18} color={hasInput ? C.tileGreenText : C.labelFaint} strokeWidth={2.5} />
          </Pressable>
        </View>
      </View>
    </View>
  );
}
